<template lang="pug">
.record-editor.flex.column.align-center.overflow-overlay(:class="{'show': showEditor}")
  .header.flex.align-center
    el-button(round, size="mini", icon="el-icon-arrow-left", @click="close(true)") 返回
  .content.card-style.flex.column.gap-10
    .column.flex.column.gap-4
      .name.text-secondary 個案
      .value.flex.align-center
        span {{ patient?.family_name }}
        span {{ patient?.given_name }}
    .column.flex.column.gap-4
      .name.text-secondary 預約時間與時長
      .value {{ $timestampToDateTimeMinuteWithDay(data.start_at) }} ({{ data.length }} hr)
    .column.flex.column.gap-4
      .name.text-secondary 型態
      .value {{ $tryMsg(`appointment.locationType.${data.location_type}`) || '---' }}
    .column.flex.column.gap-4
      .name.text-secondary 本次諮商
      .value {{ $tryMsg(`appointment.type.${data.appointment_type}`) || '---' }}
    .column.flex.column.gap-4
      .name.text-secondary 專案
      .value {{ data.project || '---' }}
    .column.flex.column.gap-4
      .name.text-secondary 議題
      .value
        template(v-if="data.main_requirement_type === 'mental'") 心理
        template(v-else-if="data.main_requirement_type === 'law'") 法律
        template(v-else-if="data.main_requirement_type === 'finance'") 財務
        template(v-else-if="data.main_requirement_type === 'management'") 組織
        template(v-else-if="data.main_requirement_type === 'medical'") 健康
        template(v-else) ---
    .column.flex.row.align-center.justify-start(v-if="!editing")
      el-button(type="primary", @click="confirmStartEditing") 編輯個案記錄
      .empty.flex-1
  .edit-content.flex.column.align-stretch.gap-30(v-if="editing")
    template(v-if="templates.length > 0")
      .rows.flex.column
        .row 專案：{{ data.project }}
        .row (需填寫專案範本, 點選連結下載範本)
        .files.flex.column
          .file.flex.align-center(v-for="file in templates", :key="file.url")
            a.flex.align-center.gap-4(target="_blank", :href="file.url")
              i.el-icon-document
              span {{ file.filename }}
      .splitter
      .upload-area.flex.column.gap-10
        .desc.text-secondary 必須將完成的個案記錄上傳，並且完成下方蛹之生的個案記錄文件
        el-button(type="primary", @click="addUpload") 上傳個案記錄
        input.file-input(
          type="file", ref="upload",
          @input="changeFile")
        .uploaded-files.flex.column.align-start.gap-6
          .file.flex.align-center.gap-6(v-for="(file, idx) in files", :key="idx")
            i.el-icon-document
            .name {{ file?.name }}
            i.el-icon-delete.danger-color(@click="deleteAttach(idx)")
      .splitter
    //- .title 議題：{{ inputSetting.label }}
    edit-block(
      ref="block"
      v-for="(block, idx) in showBlocks", :key="idx"
      :index="idx + 1",
      :valueKey="block.key",
      :title="block.title",
      :tags="block.tags",
      :minTagNum="block.minTagNum",
      :maxTagNum="block.maxTagNum",
      :require="block.require"
    )
    .buttons.flex.align-center.p-12
      el-button(@click="close") 取消
      //- 已發佈的記錄不可再存成草稿（後端回 409），只能重新發佈
      el-button(v-if="!recordPublished", type="primary", @click="save(false)") 儲存草稿
      el-button(type="primary", @click="save(true)") 完成
      .publish-block-hint.text-secondary(v-if="publishBlockMessage") {{ publishBlockMessage }}
</template>

<script>
import {
  getAppointment,
  updateAppointmentRecord,
  getRecordTags,
  getAppointmentRecord,
  getAppointmentRecordTemplate,
  addAppointmentRecordFile,
  deleteAppointmentAttachment,
} from '@/api/reserve';
import {
  getLastPatientRecordWithRecords,
} from '@/api/patient';
import { blocks, noProjectTags } from '@/site/professional/data/caseProp';
import EditBlock from '@/site/professional/components/record/EditBlock.vue';

// 後端 record_publish_block／PUT record 錯誤碼對應的提示
const PUBLISH_BLOCK_MESSAGES = {
  'appointment not started': '該預約尚未開始，無法送出，請先儲存草稿',
  // 舊版後端（以預約結束時間、付款狀態判斷）的錯誤碼，後端部署前仍可能回傳
  'appointment not finished': '該預約尚未結束，無法送出，請先儲存草稿',
  'appointment not paid': '該預約尚未付款，無法送出，請先儲存草稿',
};

export default {
  name: 'record-editor',
  components: {
    EditBlock,
  },
  watch: {
    $route() {
      this.id = undefined;
    },
  },
  data() {
    return {
      id: undefined,
      lastID: undefined,
      data: {},
      editing: false,
      projectType: 'mental',
      blocks,
      hasOld: false,
      templates: [],
      files: [],
      patient: {},
    };
  },
  computed: {
    hasProject() {
      return this.data?.project !== '';
    },
    showEditor() {
      return this.id !== undefined;
    },
    // 後端 GET /v1/appointments/:id 的 record_finished：已有發佈的個案記錄
    recordPublished() {
      return this.data?.record_finished === true;
    },
    // 後端 GET /v1/appointments/:id 的 record_publish_block：目前不能送出的原因（只能存草稿）
    publishBlockMessage() {
      return PUBLISH_BLOCK_MESSAGES[this.data?.record_publish_block] || '';
    },
    showBlocks() {
      return this.blocks.filter(
        (b) => !b.main_requirement_type
          || !this.data.main_requirement_type
          || b.main_requirement_type === this.data.main_requirement_type,
      );
    },
    // inputSetting() {
    //   return this.setting[this.projectType];
    // },
  },
  methods: {
    // 個案記錄相關錯誤（後端 PUT record／附件上傳刪除）改顯示中文說明
    showRecordError(title, e) {
      const err = e?.response?.data?.error;
      if (PUBLISH_BLOCK_MESSAGES[err]) {
        this.$showWarn(title, PUBLISH_BLOCK_MESSAGES[err]);
        return;
      }
      if (err === 'record already published') {
        this.$showError(`${title}: 此個案記錄已發佈，無法存成草稿，請按「完成」重新發佈`);
        return;
      }
      this.$showAxiosException(title, e);
    },
    confirmStartEditing() {
      if (!this.recordPublished) {
        this.startEditing();
        return;
      }
      // 已發佈的記錄重新編輯會整筆覆寫並重新產生 PDF，需額外確認
      this.$confirm('此個案記錄已發佈，重新編輯並按「完成」會覆寫原記錄並重新產生 PDF，修改前後的內容會留存於異動紀錄，確定要編輯嗎？', '編輯已發佈的個案記錄', {
        confirmButtonText: '確定編輯',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        this.startEditing();
      }).catch(() => {
        // do nothing
      });
    },
    deleteAttach(idx) {
      const removeFile = this.files[idx];
      this.$execWithLoading(async () => {
        await deleteAppointmentAttachment(this.id, removeFile.id);
        this.files.splice(idx, 1);
        this.$forceUpdate();
      }, (e) => {
        this.showRecordError('移除附件失敗', e);
      });
    },
    addUpload() {
      this.$refs.upload.click();
    },
    async changeFile() {
      if (this.$refs.upload.files.length < 1) {
        return;
      }
      await this.$execWithLoadingMessage('檔案上傳中', async () => {
        const file = this.$refs.upload.files[0];

        const rsp = await addAppointmentRecordFile(this.id, file);

        this.files.push({
          name: file.name,
          id: rsp.id,
          file,
        });
      }, (e) => {
        this.showRecordError('上傳附件失敗', e);
      });
      this.$refs.upload.value = '';
    },
    async startEditing() {
      this.editing = true;
      this.$nextTick(async () => {
        await this.$execWithLoading(async () => {
          const hasData = await this.loadCurrentRecord();
          if (!hasData && this.lastID) {
            const lastData = await getAppointmentRecord(this.lastID);
            this.$refs.block.forEach((b) => {
              const key = b.getKey();
              const tagKey = `${key}_tags`;
              if (key === 'medical_history') {
                b.setValue(lastData[key], lastData[tagKey] || []);
              }
            });
          }
        }, (e) => {
          this.$showAxiosException('載入上次紀錄失敗', e);
        });
      });
    },
    async loadCurrentRecord() {
      try {
        const rsp = await getAppointmentRecord(this.id);
        this.$refs.block.forEach((b) => {
          const key = b.getKey();
          const tagKey = `${key}_tags`;
          b.setValue(rsp[key], rsp[tagKey] || []);
        });
        this.files = (rsp.attachments || []).map((a) => ({
          name: a.filename,
          id: a.id,
        }));
        return true;
      } catch (e) {
        return false;
      }
    },
    reset() {
      this.id = undefined;
      this.data = {};
      this.editing = false;
      this.projectType = 'mental';
    },
    save(publish) {
      let canSave = true;
      const query = {};
      this.$refs.block.forEach((b) => {
        if (publish) {
          // only check when publish
          canSave = canSave && b.validate();
        }
        const value = b.getValue();
        query[value.key] = value.data;
        query[`${value.key}_tags`] = value.tags;
      });
      if (!canSave) {
        return;
      }
      // 預約尚未開始時只能存草稿
      if (publish && this.publishBlockMessage) {
        this.$showWarn('無法送出個案記錄', this.publishBlockMessage);
        return;
      }
      // 有專案範本的預約，完成（發佈）前必須上傳完成的個案記錄
      if (publish && this.templates.length > 0 && this.files.length === 0) {
        this.$showWarn('請先上傳完成的個案記錄再按完成');
        return;
      }
      query.publish = publish;
      query.attachments = this.files.map((f) => f.id);

      this.$execWithLoading(async () => {
        await updateAppointmentRecord(this.id, query);
        if (publish) {
          this.$showSuccess('已送出個案紀錄');
          this.$emit('finish');
          this.close();
        } else {
          this.$showSuccess('已儲存個案紀錄');
        }
      }, (e) => {
        this.showRecordError('儲存失敗', e);
      });
    },
    close(showConfirm = false) {
      if (showConfirm && this.editing) {
        this.$confirm('離開編輯頁面可能會遺失已編輯的內容，請確定儲存後再離開頁面', '確認關閉', {
          confirmButtonText: '離開編輯頁面',
          cancelButtonText: '回到編輯頁面',
          type: 'warning',
        }).then(() => {
          this.id = undefined;
          this.data = {};
          this.editing = false;
          this.projectType = 'mental';
          this.hasOld = false;
          this.templates = [];
          this.files = [];
          this.lastID = undefined;
        }).catch(() => {
          // do nothing
        });
        return;
      }
      this.id = undefined;
      this.data = {};
      this.editing = false;
      this.projectType = 'mental';
      this.hasOld = false;
      this.templates = [];
      this.files = [];
      this.lastID = undefined;
    },
    loadData() {
      this.$execWithLoading(async () => {
        await Promise.all([
          this.loadRecordData(),
          this.loadLastRecord(),
        ]);
      }, (e) => {
        this.$showAxiosException('載入預約資料失敗', e);
        this.close();
      });
    },
    async loadLastRecord() {
      try {
        const lastRecord = await getLastPatientRecordWithRecords(this.patient?.id);
        this.lastID = lastRecord.id;
      } catch (error) {
        console.error('Error loading last record:', error);
      }
    },
    async loadRecordData() {
      const data = await getAppointment(this.id);
      this.data = {
        ...data,
        length: ((data.end_at - data.start_at) / 3600).toFixed(1),
      };
      this.projectType = data.appointment_type;

      let tags;
      if ((data?.project || '') === '') {
        tags = noProjectTags;
      } else {
        tags = await getRecordTags(this.id);
      }
      blocks.forEach((block) => {
        const tagKey = `${block.key}_tags`;
        const values = tags[tagKey] || [];

        values.forEach((value) => {
          if (value.text === value.tag) {
            // eslint-disable-next-line no-param-reassign
            value.text = this.$msg(`tag.${block.key}.${value.tag}`);
          }
        });

        // eslint-disable-next-line no-param-reassign
        block.tags = values;
      });

      const rsp = await getAppointmentRecordTemplate(this.id);
      this.templates = rsp.map((t) => ({
        ...t,
        filename: t.filename.replace(/^\('/g, '').replace(/',\)$/g, ''),
      }));
    },
    loadMockData() {
      this.data = {
        id: 91,
        price: 1200.0,
        patient: {
          id: 5,
          family_name: 'A',
          given_name: 'CC',
        },
        start_at: 1681092000.0,
        end_at: 1681095600.0,
        length: ((1681095600.0 - 1681092000.0) / 3600).toFixed(1),
        location_type: 'virtual',
        room: {
          id: 2,
          name: '空間二',
          branch: {
            id: 1,
            name: '總館',
          },
        },
        record_finished: false,
        can_edit_record: false,
        project: '112年組織',
      };
    },
    unloadListener(e) {
      if (this.editing && !this.notSave) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    setUnloadListener() {
      window.onbeforeunload = this.unloadListener;
    },
  },
  mounted() {
    this.$on('show', (id) => {
      this.reset();
      this.setUnloadListener();
      this.id = id.id || id;
      this.patient = id.patient || {};
      this.loadData();
    });
  },
  unmounted() {
    window.onbeforeunload = null;
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.record-editor {
  height: calc(100vh - #{$header-height});
  height: calc(100dvh - #{$header-height});
  width: 100vw;
  background: #F9F9F9;
  position: absolute;
  opacity: 0.01;
  transition: all .5s ease-in-out;
  pointer-events: none;
  top: $header-height;
  left: 0;
  z-index: 2;
  &.show {
    opacity: 1;
    pointer-events: all;
  }
  .header {
    position: sticky;
    top: 0px;
    left: 0px;
    width: 100vw;
    flex: 0 0 46px;
    background: #FFFFFF;
    box-shadow: inset 0px -1px 0px #DCDFE6;
    padding: 0 20px;
    z-index: 10;
  }
  .content {
    width: calc(100vw - 40px);
    max-width: 600px;
    padding: 20px;
    margin-top: 30px;
  }
  .edit-content {
    width: 100vw;
    max-width: 600px;
    flex: 1;
    padding-top: 50px;
    padding-bottom: 50px;
    .title {
      margin-bottom: -20px;
    }
    .files {
      background: #ECF5FF;
      padding: 10px;
      .file > a {
        text-decoration: none;
        color: #409EFF;
      }
    }
  }
}
.file-input {
  max-height: 0;
  max-width: 0;
  overflow: hidden;
}
.splitter {
  width: 100%;
  flex: 0 0 1px;
  background:#DCDFE6;
}

@media only screen and (max-width: 644px) {
  .record-editor {
    height: calc(100vh - 40px);
    height: calc(100dvh - 40px);
    top: 40px;
  }
}
</style>
